// frontend/src/pages/admin/TransactionHistory.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiSearch,
  FiCalendar,
  FiTrendingUp,
  FiTrendingDown,
  FiPackage,
  FiFilter,
  FiDownload,
  FiX,
  FiEye
} from 'react-icons/fi';

const TransactionHistory = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    product_id: '',
    transaction_type: '',
    start_date: '',
    end_date: ''
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  // Summary stats
  const [stats, setStats] = useState({
    totalIn: 0,
    totalOut: 0,
    totalTransactions: 0
  });

  // ========================================
  // FETCH DATA
  // ========================================
  useEffect(() => {
    fetchHistory();
  }, [pagination.page, filters]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/inventory/history', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          product_id: filters.product_id,
          transaction_type: filters.transaction_type,
          start_date: filters.start_date,
          end_date: filters.end_date
        }
      });

      if (response.data.success) {
        setHistory(response.data.data.history);
        setPagination(response.data.data.pagination);
        
        // Calculate stats
        const inTransactions = response.data.data.history.filter(h => h.transaction_type === 'IN');
        const outTransactions = response.data.data.history.filter(h => h.transaction_type === 'OUT');
        
        setStats({
          totalIn: inTransactions.reduce((sum, t) => sum + Math.abs(t.quantity), 0),
          totalOut: outTransactions.reduce((sum, t) => sum + Math.abs(t.quantity), 0),
          totalTransactions: response.data.data.history.length
        });
      }
    } catch (error) {
      console.error('Fetch history error:', error);
      toast.error('Түүх татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products', {
        params: { page: 1, limit: 1000 }
      });

      if (response.data.success) {
        setProducts(response.data.data.products);
      }
    } catch (error) {
      console.error('Fetch products error:', error);
    }
  };

  const fetchTransactionDetail = async (transactionId) => {
    try {
      const response = await axios.get(`/api/inventory/transactions/${transactionId}`);
      
      if (response.data.success) {
        setSelectedTransaction(response.data.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Fetch detail error:', error);
      toast.error('Дэлгэрэнгүй татахад алдаа гарлаа');
    }
  };

  // ========================================
  // HANDLERS
  // ========================================
  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const resetFilters = () => {
    setFilters({
      product_id: '',
      transaction_type: '',
      start_date: '',
      end_date: ''
    });
    setPagination({ ...pagination, page: 1 });
  };

  const exportToExcel = () => {
    toast.info('Excel export функц удахгүй нэмэгдэнэ');
    // TODO: Implement Excel export
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Хөдөлгөөний түүх</h2>
          <p className="text-gray-600 text-sm mt-1">Барааны орлого, зарлагын бүртгэл</p>
        </div>
        <button
          onClick={exportToExcel}
          className="btn btn-secondary"
        >
          <FiDownload className="mr-2" />
          Excel татах
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-semibold">Нийт гүйлгээ</p>
              <p className="text-3xl font-bold text-blue-900">{stats.totalTransactions}</p>
            </div>
            <FiPackage className="text-blue-600" size={40} />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-semibold">Нийт орлого</p>
              <p className="text-3xl font-bold text-green-900">+{stats.totalIn}</p>
              <p className="text-xs text-green-600 mt-1">ширхэг</p>
            </div>
            <FiTrendingUp className="text-green-600" size={40} />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-red-50 to-red-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-semibold">Нийт зарлага</p>
              <p className="text-3xl font-bold text-red-900">-{stats.totalOut}</p>
              <p className="text-xs text-red-600 mt-1">ширхэг</p>
            </div>
            <FiTrendingDown className="text-red-600" size={40} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center">
            <FiFilter className="mr-2" />
            Шүүлтүүр
          </h3>
          <button
            onClick={resetFilters}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Цэвэрлэх
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Product Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <FiPackage className="inline mr-1" />
              Бараа
            </label>
            <select
              value={filters.product_id}
              onChange={(e) => handleFilterChange('product_id', e.target.value)}
              className="input"
            >
              <option value="">Бүх бараа</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.barcode} - {product.name}
                </option>
              ))}
            </select>
          </div>

          {/* Transaction Type Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <FiFilter className="inline mr-1" />
              Төрөл
            </label>
            <select
              value={filters.transaction_type}
              onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
              className="input"
            >
              <option value="">Бүгд</option>
              <option value="IN">Орлого</option>
              <option value="OUT">Зарлага</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <FiCalendar className="inline mr-1" />
              Эхлэх огноо
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="input"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <FiCalendar className="inline mr-1" />
              Дуусах огноо
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* History Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner-lg"></div>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-th">Огноо</th>
                <th className="table-th">Баримт №</th>
                <th className="table-th">Төрөл</th>
                <th className="table-th">Бараа</th>
                <th className="table-th text-center">Тоо ширхэг</th>
                <th className="table-th text-right">Үнэ</th>
                <th className="table-th text-right">Дүн</th>
                <th className="table-th">Харилцагч</th>
                <th className="table-th text-right">Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-12 text-gray-500">
                    <FiPackage size={48} className="mx-auto mb-3 opacity-50" />
                    <p>Хөдөлгөөн олдсонгүй</p>
                    <p className="text-xs mt-1">Шүүлтүүрээ өөрчлөөд дахин оролдоно уу</p>
                  </td>
                </tr>
              ) : (
                history.map((item, index) => {
                  const isIn = item.transaction_type === 'IN';
                  
                  return (
                    <tr key={index} className="table-row">
                      <td className="table-td">
                        <span className="text-sm">{item.transaction_date}</span>
                      </td>
                      <td className="table-td">
                        <button
                          onClick={() => fetchTransactionDetail(item.transaction_id)}
                          className={`font-mono text-sm font-semibold hover:underline ${
                            isIn ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {item.transaction_number}
                        </button>
                      </td>
                      <td className="table-td">
                        {isIn ? (
                          <span className="badge badge-green flex items-center w-fit">
                            <FiTrendingUp className="mr-1" size={12} />
                            Орлого
                          </span>
                        ) : (
                          <span className="badge badge-red flex items-center w-fit">
                            <FiTrendingDown className="mr-1" size={12} />
                            Зарлага
                          </span>
                        )}
                      </td>
                      <td className="table-td">
                        <p className="font-semibold text-gray-900">{item.product_name}</p>
                        <p className="text-xs text-gray-500">{item.barcode}</p>
                      </td>
                      <td className="table-td text-center">
                        <span className={`font-bold ${
                          isIn ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isIn ? '+' : ''}{item.quantity}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">{item.unit}</span>
                      </td>
                      <td className="table-td text-right">
                        {item.unit_price?.toLocaleString()}₮
                      </td>
                      <td className="table-td text-right font-semibold">
                        {item.total_price?.toLocaleString()}₮
                      </td>
                      <td className="table-td">
                        <span className="text-sm">
                          {item.supplier || item.customer || '-'}
                        </span>
                      </td>
                      <td className="table-td text-right">
                        <button
                          onClick={() => fetchTransactionDetail(item.transaction_id)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Дэлгэрэнгүй"
                        >
                          <FiEye size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination-container">
          <p className="pagination-info">
            Нийт {pagination.total} гүйлгээ
          </p>
          <div className="pagination-controls">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              Өмнөх
            </button>
            <span className="pagination-page-info">
              {pagination.page} / {pagination.pages}
            </span>
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.pages}
              className="pagination-button"
            >
              Дараах
            </button>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {showDetailModal && selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
};

// ========================================
// TRANSACTION DETAIL MODAL
// ========================================
const TransactionDetailModal = ({ transaction, onClose }) => {
  const isIn = transaction.transaction.transaction_type === 'IN';

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-4xl">
        <div className="modal-header">
          <h3 className="modal-title">
            {isIn ? '📥' : '📤'} Гүйлгээний дэлгэрэнгүй - {transaction.transaction.transaction_number}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX size={24} />
          </button>
        </div>

        <div className="modal-body">
          {/* Header Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b">
            <div>
              <p className="text-sm text-gray-600">Огноо</p>
              <p className="font-semibold">{transaction.transaction.transaction_date}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Төрөл</p>
              {isIn ? (
                <span className="badge badge-green flex items-center w-fit">
                  <FiTrendingUp className="mr-1" size={14} />
                  Орлого
                </span>
              ) : (
                <span className="badge badge-red flex items-center w-fit">
                  <FiTrendingDown className="mr-1" size={14} />
                  Зарлага
                </span>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-600">
                {isIn ? 'Нийлүүлэгч' : 'Харилцагч'}
              </p>
              <p className="font-semibold">
                {transaction.transaction.supplier || transaction.transaction.customer || '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Төлөв</p>
              <span className="badge badge-success">Баталгаажсан</span>
            </div>
          </div>

          {/* Items */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">
              Барааны жагсаалт ({transaction.items.length})
            </h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Бараа</th>
                    <th className="px-4 py-2 text-center text-sm font-semibold">Тоо ширхэг</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold">Нэгжийн үнэ</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold">Нийт дүн</th>
                  </tr>
                </thead>
                <tbody>
                  {transaction.items.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{item.product_name}</p>
                        <p className="text-xs text-gray-500">{item.barcode}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-bold ${
                          isIn ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isIn ? '+' : ''}{Math.abs(item.quantity)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">{item.unit}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {item.unit_price?.toLocaleString()}₮
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {item.total_price?.toLocaleString()}₮
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2">
                  <tr>
                    <td colSpan="3" className="px-4 py-3 text-right font-bold text-gray-900">
                      НИЙТ ДҮН:
                    </td>
                    <td className={`px-4 py-3 text-right font-bold text-lg ${
                      isIn ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.transaction.total_amount?.toLocaleString()}₮
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes */}
          {transaction.transaction.notes && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-semibold text-gray-700 mb-1">Тайлбар:</p>
              <p className="text-gray-900">{transaction.transaction.notes}</p>
            </div>
          )}

          {/* Created By */}
          <div className="mt-6 pt-6 border-t">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Үүсгэсэн:</p>
                <p className="font-semibold">{transaction.transaction.created_by_name}</p>
              </div>
              {isIn && transaction.transaction.received_by_name && (
                <div>
                  <p className="text-gray-600">Хүлээн авсан:</p>
                  <p className="font-semibold">{transaction.transaction.received_by_name}</p>
                </div>
              )}
              {!isIn && transaction.transaction.issued_by_name && (
                <div>
                  <p className="text-gray-600">Олгосон:</p>
                  <p className="font-semibold">{transaction.transaction.issued_by_name}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary w-full">
            Хаах
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;